import { useState } from "react";
import { Box, Stack } from "@mui/material";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format, parse } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Calendar } from "app/shadcnComponents/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "app/shadcnComponents/ui/popover";
import { faChevronDown, faCalendar } from "@fortawesome/pro-light-svg-icons";

type Props = {
  startDate?: string;
  endDate?: string;
  onChange: (
    startDate: string | undefined,
    endDate: string | undefined
  ) => void;
};

const parseLocalDate = (dateStr: string): Date =>
  parse(dateStr, "yyyy-MM-dd", new Date());

export const TeamDateRangeFilter = ({
  startDate,
  endDate,
  onChange,
}: Props) => {
  const [open, setOpen] = useState(false);

  const selected: DateRange | undefined = startDate
    ? {
        from: parseLocalDate(startDate),
        to: endDate ? parseLocalDate(endDate) : undefined,
      }
    : undefined;

  const handleSelect = (range: DateRange | undefined) => {
    const start = range?.from ? format(range.from, "yyyy-MM-dd") : undefined;
    const end = range?.to ? format(range.to, "yyyy-MM-dd") : undefined;
    onChange(start, end);
    // Only close once a real range is selected (two distinct dates).
    // react-day-picker sets from === to on a single click, so we must
    // wait for a second distinct date before dismissing the popover.
    const hasRealRange =
      range?.from && range?.to && range.from.getTime() !== range.to.getTime();
    if (hasRealRange) setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined, undefined);
    setOpen(false);
  };

  const getDateLabel = () => {
    if (!startDate) return "Date range";
    if (!endDate) return `From ${format(parseLocalDate(startDate), "MMM d")}`;
    return `${format(parseLocalDate(startDate), "MMM d")} – ${format(
      parseLocalDate(endDate),
      "MMM d"
    )}`;
  };

  const hasRange = !!startDate;

  return (
    <Stack direction="column">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Box
            component="button"
            className={hasRange ? "text-teamApp-interactive-active" : ""}
            sx={{
              display: "flex",
              alignItems: "center",
              columnGap: ".5rem",
              borderRadius: ".75rem",
              background: "white",
              border: `1px solid ${
                hasRange
                  ? "var(--team-app-interactive-active)"
                  : "var(--team-app-filter-active-border)"
              }`,
              boxSizing: "border-box",
              height: "2.5rem",
              padding: "0 .75rem",
              cursor: "pointer",
              fontSize: ".875rem",
              fontWeight: hasRange ? 600 : 400,
              whiteSpace: "nowrap",
              outline: "none",
              "&:hover": { borderColor: "var(--team-app-interactive-active)" },
            }}
          >
            <FontAwesomeIcon
              icon={faCalendar}
              className="text-xs"
              style={{ color: "var(--team-app-interactive-active)" }}
            />
            <span>{getDateLabel()}</span>
            <span className="ms-auto flex items-center gap-2">
              {hasRange && (
                <FontAwesomeIcon
                  icon={faXmark}
                  className="text-xs"
                  onClick={handleClear}
                />
              )}
              <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
            </span>
          </Box>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={selected}
            onSelect={handleSelect}
            disabled={{ after: new Date() }}
          />
        </PopoverContent>
      </Popover>
    </Stack>
  );
};
